import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, MapPin, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, productAlt } from "../lib/localize";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";

// Page d'atterrissage SEO/GEO « Trophées de hockey » (demande du propriétaire,
// 2026-10-27 : « capter les recherches les plus fréquentes de vos visiteurs —
// selon le calendrier »). Le hockey est le sport de la saison OCTOBRE → AVRIL
// (tournois d'hiver, finales de ligue, galas de fin d'année) ; une page jumelle
// « soccer » reprendra le relais pour la saison estivale (même gabarit).
// Titre/description dédiés, mots-clés ciblés, JSON-LD (CollectionPage +
// ItemList + FAQPage + fil d'Ariane), texte local (Léry, Montérégie).
const RECHERCHE = "hockey";

const KW_FR = "trophée hockey, trophées de hockey personnalisés, médaille hockey, médailles hockey personnalisées, plaque hockey gravée, trophée tournoi de hockey, trophée hockey enfant, prix hockey ligue, trophée gardien de but, trophée hockey Québec, médailles hockey tournoi, trophées hockey Montréal rive-sud, trophée hockey Léry, trophée hockey pas cher, gravure trophée hockey, coupe hockey personnalisée";
const KW_EN = "hockey trophy, personalized hockey trophies, hockey medal, custom hockey medals, engraved hockey plaque, hockey tournament trophy, kids hockey trophy, hockey league awards, goalie trophy, hockey trophies Quebec, hockey medals Montreal South Shore, hockey trophy Lery, custom hockey cup, engraved hockey trophy Canada";

const FAQ = [
  {
    fr: ["Combien coûte un trophée de hockey personnalisé avec la gravure ?",
         "Nos médailles de hockey commencent à quelques dollars et nos trophées de tournoi restent très abordables — la gravure personnalisée du nom du joueur, de l'équipe et de l'année est confirmée avec vous avant la production, sans surprise. Écrivez-nous votre quantité : la soumission est gratuite et répondue en moins de 24 h."],
    en: ["How much does a personalized hockey trophy with engraving cost?",
         "Our hockey medals start at just a few dollars and our tournament trophies stay very affordable — the personalized engraving of the player's name, team and year is confirmed with you before production, no surprises. Tell us your quantity: the quote is free and answered within 24 hours."],
  },
  {
    fr: ["Faites-vous les trophées pour les tournois de hockey et les ligues ?",
         "Oui, c'est notre spécialité de saison : médailles pour tous les participants, trophées pour les champions et finalistes, prix d'excellence (meilleur joueur, meilleur gardien, esprit sportif) et plaques gravées pour les bénévoles. Nous produisons des dizaines à des centaines de pièces par tournoi, avec le nom de votre événement gravé."],
    en: ["Do you make trophies for hockey tournaments and leagues?",
         "Yes — it's our in-season specialty: participation medals for every player, champion and finalist trophies, excellence awards (MVP, best goalie, sportsmanship) and engraved plaques for volunteers. We produce dozens to hundreds of pieces per tournament, with your event's name engraved."],
  },
  {
    fr: ["Quel délai prévoir avant mon tournoi ou mon gala de hockey ?",
         "Prévoyez idéalement 2 à 3 semaines ; un tournoi de dernière minute ? Écrivez-nous — nous avons souvent des solutions rapides en stock (médailles et trophées hockey prêts à graver). Chaque commande affiche une date de livraison claire, respectée."],
    en: ["How long before my hockey tournament or awards gala?",
         "Ideally allow 2 to 3 weeks; last-minute tournament? Write to us — we often have fast in-stock solutions (hockey medals and trophies ready to engrave). Every order shows a clear delivery date, and we honour it."],
  },
  {
    fr: ["Où êtes-vous situés et livrez-vous partout au Québec et au Canada ?",
         "Notre atelier de gravure est à Léry, en Montérégie (rive-sud de Montréal). Nous livrons partout au Québec et au Canada par Postes Canada ou messagerie, et le ramassage à l'atelier est possible sur rendez-vous."],
    en: ["Where are you located, and do you ship across Quebec and Canada?",
         "Our engraving workshop is in Léry, Montérégie (Montreal's South Shore). We ship anywhere in Quebec and Canada via Canada Post or courier, and workshop pickup is available by appointment."],
  },
];

export default function TropheesHockey() {
  const { lang } = useLang();
  const fr = lang === "fr";
  const [produits, setProduits] = useState([]);

  useEffect(() => {
    api.get("/products/browse", { params: { q: RECHERCHE, limit: 60 } })
      .then(({ data }) => setProduits(data.products || []))
      .catch(() => {});
  }, []);

  const titre = fr
    ? "Trophées et médailles de hockey personnalisés | Mr Trophée"
    : "Personalized hockey trophies and medals | Mr Trophée";
  const desc = fr
    ? "Trophées de hockey personnalisés pour tournois, ligues et galas : médailles, coupes et plaques gravées (joueur, équipe, année). Prix d'équipe abordables, délais clairs. Atelier à Léry, Montérégie — livraison partout au Canada."
    : "Personalized hockey trophies for tournaments, leagues and galas: engraved medals, cups and plaques (player, team, year). Affordable team pricing, clear lead times. Workshop in Léry, Montérégie — shipping across Canada.";

  const breadcrumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: fr ? "Accueil" : "Home", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: fr ? "La Boutique" : "Shop", item: "https://mrtrophee.ca/boutique" },
      { "@type": "ListItem", position: 3, name: fr ? "Trophées de hockey" : "Hockey trophies", item: "https://mrtrophee.ca/trophees-hockey" },
    ],
  };
  const collectionLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: fr ? "Trophées et médailles de hockey personnalisés" : "Personalized hockey trophies and medals",
    description: desc, url: "https://mrtrophee.ca/trophees-hockey",
    inLanguage: fr ? "fr-CA" : "en-CA",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: produits.length,
      itemListElement: produits.slice(0, 40).map((p, i) => ({
        "@type": "ListItem", position: i + 1,
        url: `https://mrtrophee.ca/produit/${p.id}`,
        name: lp(p, lang).name,
      })),
    },
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: (fr ? f.fr : f.en)[0],
      acceptedAnswer: { "@type": "Answer", text: (fr ? f.fr : f.en)[1] },
    })),
  };

  return (
    <div className="bg-ink min-h-screen" data-testid="hockey-page">
      <SEO title={titre} description={desc} path="/trophees-hockey"
        keywords={fr ? KW_FR : KW_EN} jsonLd={[breadcrumbLd, collectionLd, faqLd]} />

      {/* Héros local (GEO : Léry, Montérégie, rive-sud de Montréal) */}
      <section className="border-b border-gold/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-gold/80 mb-4" data-testid="hockey-kicker">
              {fr ? "La saison de hockey — octobre à avril" : "Hockey season — October to April"}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-ivory leading-tight max-w-3xl" data-testid="hockey-h1">
              {fr
                ? <>Trophées de hockey <span className="text-gold italic">qui marquent la saison</span></>
                : <>Hockey trophies <span className="text-gold italic">that mark the season</span></>}
            </h1>
            <p className="mt-5 text-ivory/70 max-w-2xl text-base leading-relaxed" data-testid="hockey-intro">
              {fr
                ? "Médailles, coupes et plaques gravées pour tournois, ligues et galas de fin de saison — nom du joueur, équipe et année gravés avec soin. Prix d'équipe abordables, production à Léry (Montérégie, rive-sud de Montréal) et livraison partout au Canada. Soumission gratuite, réponse en moins de 24 h."
                : "Engraved medals, cups and plaques for tournaments, leagues and season-end galas — player name, team and year engraved with care. Affordable team pricing, made in Léry (Montérégie, Montreal's South Shore) and shipped across Canada. Free quote, answered within 24 hours."}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#modeles" data-testid="hockey-cta-modeles"
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 rounded-full hover:bg-champagne transition-colors duration-300 text-sm">
                {fr ? "Voir les modèles de hockey" : "See the hockey models"} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/soumission-rapide" data-testid="hockey-cta-soumission"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm">
                {fr ? "Soumission en 30 secondes" : "30-second quote"}
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Atouts (texte riche pour moteurs et IA) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Award,       fr: ["Tout pour le tournoi", "Médailles de participation, trophées champions/finalistes, prix d'excellence et plaques de bénévoles — un seul fournisseur pour tout l'événement."],
                                 en: ["Everything for the tournament", "Participation medals, champion/finalist trophies, excellence awards and volunteer plaques — one supplier for the whole event."] },
            { icon: ShieldCheck, fr: ["Gravure personnalisée", "Nom du joueur, équipe et année gravés proprement — le prix est confirmé avant la production, sans surprise."],
                                 en: ["Personalized engraving", "Player name, team and year engraved cleanly — the price is confirmed before production, no surprises."] },
            { icon: Sparkles,    fr: ["Délais de tournoi", "Idéalement 2 à 3 semaines ; dernier droit ? Médailles et trophées de hockey en stock, prêts à graver vite."],
                                 en: ["Tournament-ready lead times", "Ideally 2 to 3 weeks; last minute? Hockey medals and trophies in stock, ready to engrave fast."] },
            { icon: Truck,       fr: ["Livraison Canada", "Expédition par Postes Canada ou messagerie, partout au pays. Ramassage gratuit à l'atelier de Léry sur rendez-vous."],
                                 en: ["Canada-wide shipping", "Shipped by Canada Post or courier anywhere in the country. Free workshop pickup in Léry by appointment."] },
          ].map((b, i) => {
            const Icon = b.icon;
            const [h, p] = fr ? b.fr : b.en;
            return (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="bg-surface/60 border border-ivory/10 rounded-2xl p-6 h-full" data-testid={`hockey-atout-${i}`}>
                  <Icon className="w-6 h-6 text-gold mb-3" />
                  <h2 className="font-serif text-lg text-ivory mb-1.5">{h}</h2>
                  <p className="text-sm text-ivory/65 leading-relaxed">{p}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* Grille des modèles */}
      <section id="modeles" className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <FadeUp>
          <h2 className="font-serif text-3xl text-ivory mb-2" data-testid="hockey-modeles-h2">
            {fr ? "Nos modèles de hockey" : "Our hockey models"}
          </h2>
          <p className="text-sm text-ivory/60 mb-8">
            {fr
              ? "Cliquez un modèle pour voir les grandeurs et lancer votre demande — chaque pièce est gravée à votre événement."
              : "Click a model to see sizes and start your request — every piece is engraved for your event."}
          </p>
        </FadeUp>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="hockey-grid">
          {produits.map((p) => (
            <Link key={p.id} to={`/produit/${p.id}`} data-testid={`hockey-product-${p.id}`}
              className="group bg-surface/60 border border-ivory/10 rounded-2xl overflow-hidden hover:border-gold/50 transition-colors duration-300">
              <div className="aspect-square bg-ink overflow-hidden">
                {p.image && (
                  <img src={p.image} alt={productAlt(p, lang)} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-ivory leading-snug line-clamp-2 group-hover:text-gold transition-colors">
                  {lp(p, lang).name}
                </p>
                <p className="mt-1 text-sm text-champagne font-semibold">{fmt(p.base_price)}</p>
              </div>
            </Link>
          ))}
        </div>
        {!produits.length && (
          <p className="text-ivory/50 text-sm py-10 text-center" data-testid="hockey-vide">
            {fr ? "Chargement des modèles…" : "Loading models…"}
          </p>
        )}
      </section>

      {/* FAQ (réponses des IA / position zéro) */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-20">
        <FadeUp>
          <h2 className="font-serif text-3xl text-ivory mb-8" data-testid="hockey-faq-h2">
            {fr ? "Questions fréquentes — trophées de hockey" : "Frequently asked questions — hockey trophies"}
          </h2>
        </FadeUp>
        <div className="space-y-5">
          {FAQ.map((f, i) => {
            const [q, r] = fr ? f.fr : f.en;
            return (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="bg-surface/60 border border-ivory/10 rounded-2xl p-6" data-testid={`hockey-faq-${i}`}>
                  <h3 className="font-serif text-lg text-gold mb-2">{q}</h3>
                  <p className="text-sm text-ivory/70 leading-relaxed">{r}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
        <FadeUp delay={0.15}>
          <div className="mt-10 text-center">
            <Link to="/soumission-rapide" data-testid="hockey-cta-bas"
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
              {fr ? "Obtenir ma soumission hockey" : "Get my hockey quote"} <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-xs text-ivory/50 flex items-center justify-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {fr ? "Atelier à Léry, Montérégie — rive-sud de Montréal" : "Workshop in Léry, Montérégie — Montreal's South Shore"}
            </p>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
